import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { IconName } from "app/shared/models/icon-name.type";
import { ThemePalette } from "@angular/material/core";
import { IconSize } from "app/shared/models/icon-size.type";
import { TranslationKey } from "translations/translation-key.type";
import { takeUntil, tap, throttleTime } from "rxjs/operators";
import { Subject } from "rxjs";
import { MatTooltip } from "@angular/material/tooltip";
import { MatButton, MatIconButton } from "@angular/material/button";
import { NgClass, NgIf, NgTemplateOutlet } from "@angular/common";
import { MatMenuItem } from "@angular/material/menu";
import { TranslateModule } from "@ngx-translate/core";
import { SvgComponent } from "app/shared/components/svg/svg-icon/svg-icon.component";
import { LetterCasePipe } from "app/shared/pipes/letter-case.pipe";
import {
  AppButtonSize,
  AppButtonType,
} from "app/shared/components/button/button.model";
import { HoverboxComponent } from "app/shared/components/hoverbox/hoverbox.component";
import { HoverboxDirective } from "app/shared/directives/hoverbox";

/*
 * The component responsible to provide icon button , raise button , submit button
 *
 * @example of Icon Button
 * <app-button
 *  [matMenuTriggerFor]="devicesMenu"
 * iconName="tune"
 * hoverTitle="toolbar.buttons.device_settings.tooltip"
 * iconFillClass="header-button"
 * ></app-button>
 *
 *  @example of basic-button
 * <app-button
 * (onClick)="cancelEditFaxCover()"
 *  text="cancel"
 * type="basic-button"
 * ></app-button>
 *
 * @example of raised-button-submit
 * <app-button
 * [disabled]="!coverForm.valid"
 * (onClick)="onSaveFaxCover()"
 *  text="save"
 *  type="raised-button-submit"
 * ></app-button>
 */

@Component({
  selector: "app-button",
  templateUrl: "./button.component.html",
  styleUrls: ["./button.component.scss"],
  imports: [
    MatTooltip,
    MatIconButton,
    SvgComponent,
    NgClass,
    NgIf,
    MatMenuItem,
    MatButton,
    TranslateModule,
    LetterCasePipe,
    NgTemplateOutlet,
    HoverboxComponent,
    HoverboxDirective,
  ],
})
export class ButtonComponent implements OnChanges, OnInit, OnDestroy {
  private iconFillInitial: string = "button-color";
  private clicks: Subject<void> = new Subject();
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  // All properties which handle the "disabled"-states
  public isButtonDisabled: boolean = false;

  @Input()
  public disabled: boolean;

  // Disabled icon color can be set here
  @Input()
  public disabledColor: string = "grey-color";

  // Used when the background for the disabled state should be transparent
  @Input()
  public transparentOnDisabled: boolean = false;

  // All properties which handle the texts and their styles
  @Input()
  public buttonTextTranslationKey: TranslationKey;

  @Input()
  public hoverTextTranslationKey: TranslationKey;

  // Some buttons have very important hover text which the user should always see with disregard of his helpmode settings
  @Input()
  public isHoverboxVisible: boolean = false;

  @Input()
  public textColor: string = "default-color";

  public underlinedColor: string;

  // All properties which handle the buttons and their styles
  /*
   * We always need to use (appButtonClick) and should never use (click) on an app-button component
   * If we'd use (click) directly on an app-button like this: <app-button (click)="doSomething()">, then the disabled state doesn't matter at all.
   * In conclusion the user could always click on a button, no matter if the state is disabled or not.
   */
  @Output() public appButtonClick: EventEmitter<void> =
    new EventEmitter<void>();

  @Input()
  public color: ThemePalette;

  @Input()
  public tabindex: string = "";

  @Input()
  public size: AppButtonSize = "button--fullwidth";

  // If no type is set, it will be considered as a button with an icon
  @Input()
  public type: AppButtonType = "";

  @Input()
  public raiseHandTooltipClass?: string;

  public buttonType: "submit" | "button";

  // Will be set to true for stroked-button or stroked-button-submit
  public isStrokedButton: boolean;

  // Will be set to true for raised-button or raised-button-submit
  public isRaisedButton: boolean;

  public isBasicButton: boolean;

  public isBasicIconTextButton: boolean;

  // Used for E2E testing. This value is set as `data-e2e` HTML attribute.
  @Input()
  public e2e: string;

  // All properties which handle the "icon"-states
  @Input()
  public iconFill: string = "button-color";

  @Input()
  public iconFillClass: string;

  @Input()
  public iconName: IconName = "contact";

  @Input()
  public iconSize: IconSize = "small";

  // Displays a line under the icon
  @Input()
  public iconUnderlinedColor: number | null = null;

  @Input()
  public hasDebounce: boolean = false;

  public ngOnInit(): void {
    this.clicks
      .pipe(
        tap(() => (this.isButtonDisabled = true)),
        throttleTime(this.hasDebounce ? 500 : 0),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(() => {
        this.isButtonDisabled = false;
        this.appButtonClick.emit();
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty("iconFillClass")) {
      this.checkIconFillClass(changes);
    }
    if (changes.hasOwnProperty("disabled")) {
      this.checkDisabledState(changes);
    }
    if (changes.hasOwnProperty("type")) {
      this.checkType();
    }
    if (changes.hasOwnProperty("iconUnderlinedColor")) {
      this.checkIconUnderlinedColor();
    }
  }

  public onAppButtonClick(): void {
    this.clicks.next();
  }

  private checkIconFillClass(changes: SimpleChanges): void {
    this.iconFillInitial = changes.iconFillClass.currentValue;
    this.iconFill = changes.iconFillClass.currentValue;
    this.checkIfButtonDisabled();
  }

  private checkDisabledState(changes: SimpleChanges): void {
    this.isButtonDisabled = changes.disabled.currentValue;
    this.checkIfButtonDisabled();
  }

  private checkType(): void {
    this.isStrokedButton = this.type.includes("stroked-button");
    this.isRaisedButton = this.type.includes("raised-button");
    this.isBasicButton = this.type.includes("basic-button");
    this.isBasicIconTextButton = this.type.includes("basic-icon-text-button");
    this.buttonType = this.type.indexOf("submit") > -1 ? "submit" : "button";
  }

  private checkIconUnderlinedColor(): void {
    this.underlinedColor = this.iconUnderlinedColor
      ? `app-button__underline--${this.iconUnderlinedColor}`
      : "";
  }

  private checkIfButtonDisabled(): void {
    if (this.isButtonDisabled) {
      this.iconFillInitial = this.iconFill;
      this.iconFill = this.disabledColor;
    } else {
      this.iconFill = this.iconFillInitial;
    }
  }
}
